import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { CheckCircle2, XCircle, Loader2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('checking');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!sessionId) {
      setStatus('error');
      setError('Missing session id');
      return;
    }
    let attempts = 0;
    const max = 6;
    let timer = null;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`, { withCredentials: true });
        setInfo(data);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        if (attempts < max) {
          attempts += 1;
          timer = setTimeout(poll, 2000);
        } else {
          setStatus('timeout');
        }
      } catch (e) {
        setStatus('error');
        setError(e.response?.data?.detail || e.message);
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId, user, authLoading, navigate]);

  return (
    <DashboardLayout>
      <div className="p-8 max-w-2xl mx-auto" data-testid="billing-success-page">
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 text-[#002FA7] mx-auto mb-5 animate-spin" strokeWidth={2} />
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Finalizing your subscription…
              </h1>
              <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                We&apos;re confirming the payment with Stripe. This usually takes just a moment.
              </p>
            </>
          )}
          {status === 'paid' && (
            <>
              <CheckCircle2 className="w-14 h-14 text-[#34C759] mx-auto mb-5" strokeWidth={2} />
              <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                You&apos;re all set!
              </h1>
              <p className="text-sm text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Your <span className="font-semibold">{info?.plan_name || 'subscription'}</span> plan is active.
                Welcome aboard.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="success-go-dashboard"
                className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#0034BD] text-white px-6 py-3 rounded-md font-semibold transition-colors"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Go to dashboard <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </>
          )}
          {(status === 'expired' || status === 'timeout') && (
            <>
              <XCircle className="w-14 h-14 text-amber-500 mx-auto mb-5" strokeWidth={2} />
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                {status === 'expired' ? 'Session expired' : 'Still processing'}
              </h1>
              <p className="text-sm text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {status === 'expired'
                  ? 'Your checkout session expired. You can try again from billing.'
                  : 'Payment is taking longer than expected. Refresh this page in a moment or check your email.'}
              </p>
              <button
                onClick={() => navigate('/billing')}
                className="text-[#002FA7] font-semibold hover:underline"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                Back to plans
              </button>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="w-14 h-14 text-red-500 mx-auto mb-5" strokeWidth={2} />
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Something went wrong</h1>
              <p className="text-sm text-red-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</p>
              <button onClick={() => navigate('/billing')} className="text-[#002FA7] font-semibold hover:underline">Back to plans</button>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export const BillingCancel = () => {
  const navigate = useNavigate();
  return (
    <DashboardLayout>
      <div className="p-8 max-w-2xl mx-auto" data-testid="billing-cancel-page">
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <XCircle className="w-14 h-14 text-slate-400 mx-auto mb-5" strokeWidth={2} />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Checkout canceled</h1>
          <p className="text-sm text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            No charges were made. You can pick a plan whenever you&apos;re ready.
          </p>
          <button
            onClick={() => navigate('/billing')}
            className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#0034BD] text-white px-6 py-3 rounded-md font-semibold transition-colors"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            View plans <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

const Billing = () => {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [pRes, sRes] = await Promise.all([
          axios.get(`${API}/pricing/plans`),
          axios.get(`${API}/billing/subscription`, { withCredentials: true }),
        ]);
        if (mounted) {
          setPlans(pRes.data);
          setSubscription(sRes.data.subscription);
        }
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const subscribe = async (planId) => {
    setBusyPlan(planId);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/checkout/session`,
        { plan_id: planId, origin_url: window.location.origin },
        { withCredentials: true },
      );
      window.location.href = data.url;
    } catch (e) {
      setBusyPlan(null);
      setError(e.response?.data?.detail || e.message);
    }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-slate-600">Loading plans…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-8 max-w-6xl mx-auto" data-testid="billing-page">
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Plans & Billing</h1>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Pick the plan that fits your fleet. You can change anytime.
          </p>
        </div>

        {subscription && (
          <div data-testid="current-subscription-banner" className="mb-8 p-5 bg-[#34C759]/10 border border-[#34C759]/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-[#34C759]" strokeWidth={2} />
              <div>
                <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Current plan: {subscription.plan_name}</p>
                <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status: {subscription.status}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div data-testid="billing-error" className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const isCurrent = subscription?.plan_id === plan.id && subscription?.status === 'active';
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative bg-white border rounded-xl p-7 flex flex-col ${plan.popular ? 'border-[#002FA7] shadow-[0_8px_32px_-12px_rgba(0,47,167,0.4)]' : 'border-slate-200'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#002FA7] text-white text-xs font-semibold rounded-full" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    Most popular
                  </div>
                )}
                <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{plan.name}</h3>
                <p className="text-sm text-slate-600 mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{plan.blurb}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>${plan.price.toFixed(0)}</span>
                  <span className="text-sm text-slate-500 ml-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>/ {plan.interval}</span>
                </div>
                <ul className="space-y-2.5 mb-7 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <CheckCircle2 className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`subscribe-${plan.id}-button`}
                  disabled={isCurrent || busyPlan === plan.id || !user || user.role === 'driver' || user.role === 'dispatcher'}
                  onClick={() => subscribe(plan.id)}
                  className={`w-full py-3 rounded-md font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${plan.popular ? 'bg-[#002FA7] hover:bg-[#0034BD] text-white' : 'border border-slate-300 text-slate-900 hover:border-[#002FA7] hover:text-[#002FA7]'}`}
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {isCurrent ? 'Current plan' : busyPlan === plan.id ? 'Redirecting…' : 'Subscribe'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Billing;
